import { useState } from 'react';
import { useTenant } from '../../context/TenantContext';
import { functions } from '../../services/firebase';
import { httpsCallable } from 'firebase/functions';
import { getTenantW2Employees } from '../../services/crmService';
import { getPendingLiabilities } from '../../services/db'; // <-- NEW: Connecting the Treasury

export default function PayrollCenter() {
  const { tenantId, loading: tenantLoading } = useTenant();
  
  const [isCompiling, setIsCompiling] = useState(false);
  const [hasCompiled, setHasCompiled] = useState(false);
  
  const [payrollState, setPayrollState] = useState({
    periodStart: '2026-08-02',
    periodEnd: '2026-08-15',
    payDay: '2026-08-21'
  });

  const [stagedLedger, setStagedLedger] = useState([]);
  
  // Modals & Async States
  const [stagingModal, setStagingModal] = useState({
    isOpen: false,
    step: 'idle', 
    message: ''
  });
  
  const [isProvisioning, setIsProvisioning] = useState(false);

  // ==========================================
  // LIVE FIREBASE & CHECKHQ EXECUTIONS
  // ==========================================
  const provisionCompanyFn = httpsCallable(functions, 'provisionCheckCompany');
  const createEmployeeFn = httpsCallable(functions, 'createCheckEmployee');
  const submitPayrollFn = httpsCallable(functions, 'submitPayrollToCheckHQ');

  // --- ⚡️ 1. COMPILE DATA: TIMESHEETS + COMMISSIONS ---
  const handleCompile = async () => {
    if (!tenantId) return;
    setIsCompiling(true);
    try {
      // 1. Pull live W-2 employee data
      const employees = await getTenantW2Employees(tenantId);
      
      // 2. Pull live pending commissions from the Master Rules Engine
      const liabilities = await getPendingLiabilities(tenantId) || [];
      
      // 3. Marry the operational data with the financial data
      const ledger = employees.map(emp => {
        const fullName = `${emp.firstName || ''} ${emp.lastName || ''}`.trim();
        
        // Match the pending liability to the specific employee
        const repLiability = liabilities.find(l => l.rep === fullName || l.repId === emp.id);
        const commOwed = repLiability ? Number(repLiability.pending) : 0;

        return {
          id: emp.id,
          name: fullName,
          status: emp.checkhq_id ? 'VAULTED' : 'PENDING SYNC',
          reg: 0, // In full prod, this pulls from timesheets
          ot: 0,
          base: 0, 
          comm: commOwed, // <-- LIVE INJECTION FROM MATRIX
          gross: commOwed, // Base gross starts with commissions owed
          checkhq_id: emp.checkhq_id || null,
          selected: !!emp.checkhq_id 
        };
      });

      setStagedLedger(ledger);
      setHasCompiled(true);
    } catch (error) {
      console.error("Failed to compile ledger:", error);
      alert("System Error: Could not compile live ledger from database.");
    } finally {
      setIsCompiling(false);
    }
  };

  // 2. Mint Corporate Entity to CheckHQ
  const handleMintCompany = async () => {
    if (!tenantId) return;
    if (!window.confirm("Initialize CheckHQ Company Provisioning for this tenant?")) return;
    
    setIsProvisioning(true);
    try {
      const response = await provisionCompanyFn({ tenantId });
      if (response.data?.success) {
        alert("Success: Company minted in CheckHQ! ID saved to database.");
      } else {
        alert(`CheckHQ Error: ${response.data?.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error("CheckHQ Company Mint Failed:", error);
      alert("Failed to reach CheckHQ provisioning server.");
    } finally {
      setIsProvisioning(false);
    }
  };

  // 3. Mint Selected Employees to CheckHQ
  const handleMintEmployees = async () => {
    const unvaulted = stagedLedger.filter(e => e.selected && !e.checkhq_id);
    if (unvaulted.length === 0) {
      alert("No un-vaulted employees selected for syncing.");
      return;
    }

    setIsProvisioning(true);
    try {
      for (const emp of unvaulted) {
        await createEmployeeFn({ tenantId, employeeId: emp.id });
      }
      alert("Employee batch sync complete. Re-compiling ledger...");
      await handleCompile(); 
    } catch (error) {
      console.error("Employee Mint Failed:", error);
      alert("An error occurred while dispatching employees to CheckHQ.");
    } finally {
      setIsProvisioning(false);
    }
  };

  // 4. Execute Payroll Run
  const executeCheckHqPayload = async () => {
    const activePayload = stagedLedger.filter(e => e.selected && e.checkhq_id);
    if (activePayload.length === 0) {
      alert("Critical Error: No vaulted employees selected for payroll run.");
      return;
    }

    setStagingModal({ isOpen: true, step: 'processing', message: 'Translating Data to Check Schema...' });
    
    try {
      const payload = {
        tenantId,
        periodStart: payrollState.periodStart,
        periodEnd: payrollState.periodEnd,
        payDay: payrollState.payDay,
        items: activePayload.map(emp => ({
          employeeId: emp.checkhq_id, 
          regularHours: emp.reg,
          overtimeHours: emp.ot,
          commission: emp.comm,
          gross: emp.gross
        }))
      };

      const response = await submitPayrollFn(payload);
      
      if (response.data?.success) {
        setStagingModal({ isOpen: true, step: 'success', message: 'CheckHQ API Payload Accepted and processing.' });
      } else {
        setStagingModal({ isOpen: true, step: 'error', message: response.data?.error || 'CheckHQ rejected the payload.' });
      }
    } catch (error) {
      console.error("Payroll API Failure:", error);
      setStagingModal({ isOpen: true, step: 'error', message: 'Connection to CheckHQ API failed.' });
    }
  };

  const toggleSelect = (id) => {
    setStagedLedger(prev => prev.map(emp => emp.id === id ? { ...emp, selected: !emp.selected } : emp));
  };

  const updateLedgerValue = (id, field, value) => {
    setStagedLedger(prev => prev.map(emp => {
      if (emp.id === id) {
        const updated = { ...emp, [field]: Number(value) };
        updated.gross = updated.base + updated.comm + (updated.reg * 20); // Simplified gross calc for manual entry
        return updated;
      }
      return emp;
    }));
  };

  const calculateTotalLiability = () => {
    return stagedLedger.filter(e => e.selected).reduce((sum, e) => sum + e.gross, 0);
  };

  if (tenantLoading) return <div className="p-12 text-center text-slate-500 font-bold uppercase tracking-widest"><div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>Authenticating Gateway...</div>;

  return (
    <div className="animate-in fade-in duration-500 pb-24 relative h-full flex flex-col">
      
      {/* Header */}
      <div className="flex justify-between items-end mb-6 border-b border-slate-300 pb-4 shrink-0">
        <div>
          <h1 className="text-2xl font-black text-slate-900 tracking-tight uppercase">Payroll Center</h1>
          <p className="text-slate-500 text-sm mt-1 font-medium">Execute live CheckHQ API calls and manage workforce liabilities.</p>
        </div>
      </div>

      {/* Pay Period Configurator */}
      <div className="bg-white border border-slate-300 shadow-sm mb-6 shrink-0">
        <div className="bg-slate-100 border-b border-slate-300 px-6 py-4 flex justify-between items-center">
          <div>
            <h3 className="text-[11px] font-black text-slate-800 uppercase tracking-widest">Configure Pay Period</h3>
            <p className="text-[9px] font-bold text-slate-500 uppercase tracking-widest mt-0.5">Select date range to aggregate timesheets and sales commissions.</p>
          </div>
          <button 
            onClick={handleCompile}
            disabled={isCompiling}
            className="bg-slate-900 hover:bg-slate-800 text-white text-[10px] font-bold py-2 px-6 transition-colors uppercase tracking-widest border border-slate-900 shadow-sm flex items-center gap-2 disabled:opacity-50"
          >
            {isCompiling ? <><i className="fa-solid fa-spinner animate-spin"></i> Fetching Database...</> : <><i className="fa-solid fa-layer-group"></i> Compile Live Ledger</>}
          </button>
        </div>
        
        <div className="p-6 grid grid-cols-3 gap-8">
          <div>
            <label className="block text-[9px] font-bold text-slate-400 uppercase tracking-widest mb-1.5">Period Start</label>
            <input type="date" value={payrollState.periodStart} onChange={(e) => setPayrollState({...payrollState, periodStart: e.target.value})} className="w-full text-sm font-bold text-slate-800 border border-slate-300 px-3 py-2 focus:outline-none focus:border-emerald-500" />
          </div>
          <div>
            <label className="block text-[9px] font-bold text-slate-400 uppercase tracking-widest mb-1.5">Period End</label>
            <input type="date" value={payrollState.periodEnd} onChange={(e) => setPayrollState({...payrollState, periodEnd: e.target.value})} className="w-full text-sm font-bold text-slate-800 border border-slate-300 px-3 py-2 focus:outline-none focus:border-emerald-500" />
          </div>
          <div>
            <label className="block text-[9px] font-bold text-slate-400 uppercase tracking-widest mb-1.5 text-emerald-600">Pay Day</label>
            <input type="date" value={payrollState.payDay} onChange={(e) => setPayrollState({...payrollState, payDay: e.target.value})} className="w-full text-sm font-bold text-emerald-700 border border-emerald-300 bg-emerald-50 px-3 py-2 focus:outline-none focus:border-emerald-500" />
          </div>
        </div>
      </div>

      {/* Staged Ledger */}
      <div className="bg-white border border-slate-300 shadow-sm flex-1 flex flex-col min-h-0 relative mb-6">
        <div className="bg-slate-50 border-b border-slate-300 px-4 py-3 flex justify-between items-center shrink-0">
          <div className="flex items-center gap-2">
            <h3 className="text-[10px] font-black text-slate-600 uppercase tracking-widest">Staged Employee Ledger</h3>
          </div>
          <div className="relative">
            <i className="fa-solid fa-magnifying-glass absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-[10px]"></i>
            <input type="text" placeholder="Search employees..." className="pl-8 pr-3 py-1 text-[10px] font-bold text-slate-800 border border-slate-300 w-48 uppercase tracking-widest focus:outline-none focus:border-emerald-500 placeholder:text-slate-400" />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto custom-scrollbar relative">
          {!hasCompiled ? (
            <div className="absolute inset-0 flex flex-col items-center justify-center p-12 text-center">
              <i className="fa-solid fa-server text-4xl text-slate-200 mb-4"></i>
              <h2 className="text-sm font-black text-slate-800 uppercase tracking-widest mb-1">Awaiting Compilation</h2>
              <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest max-w-sm mx-auto leading-relaxed">
                Click "Compile Live Ledger" to pull active W-2 records from the database.
              </p>
            </div>
          ) : (
            <table className="w-full text-left border-collapse">
              <thead className="bg-white sticky top-0 z-10 shadow-sm border-b border-slate-200">
                <tr>
                  <th className="w-12 px-4 py-3 text-center border-r border-slate-100">
                    <i className="fa-solid fa-check-double text-slate-300"></i>
                  </th>
                  <th className="px-4 py-3 text-[9px] font-black text-slate-500 uppercase tracking-widest">Employee Name</th>
                  <th className="px-4 py-3 text-[9px] font-black text-slate-500 uppercase tracking-widest">CheckHQ Status</th>
                  <th className="px-4 py-3 text-[9px] font-black text-slate-500 uppercase tracking-widest">Reg Hours</th>
                  <th className="px-4 py-3 text-[9px] font-black text-slate-500 uppercase tracking-widest">Commission</th>
                  <th className="px-4 py-3 text-[9px] font-black text-slate-500 uppercase tracking-widest text-right">Gross Pay</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 bg-white">
                {stagedLedger.map((emp) => (
                  <tr key={emp.id} className={`transition-colors ${emp.selected ? 'hover:bg-slate-50' : 'bg-slate-50 opacity-60'}`}>
                    <td className="px-4 py-3 text-center border-r border-slate-100">
                      <input 
                        type="checkbox" 
                        checked={emp.selected} 
                        onChange={() => toggleSelect(emp.id)}
                        className="w-3 h-3 accent-emerald-600 cursor-pointer" 
                      />
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs font-bold text-slate-900 block">{emp.name}</span>
                      <span className="text-[8px] font-mono text-slate-400">{emp.id.substring(0,8)}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-[8px] font-black px-2 py-0.5 uppercase tracking-widest ${
                        emp.status === 'VAULTED' ? 'bg-emerald-100 text-emerald-700 border border-emerald-200' : 'bg-amber-100 text-amber-700 border border-amber-200'
                      }`}>
                        {emp.status}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <input 
                        type="number" 
                        value={emp.reg} 
                        onChange={(e) => updateLedgerValue(emp.id, 'reg', e.target.value)}
                        className="w-20 text-xs font-bold text-slate-700 border border-slate-200 px-2 py-1 focus:outline-none focus:border-emerald-500" 
                      />
                    </td>
                    <td className="px-4 py-3">
                      <input 
                        type="number" 
                        value={emp.comm} 
                        onChange={(e) => updateLedgerValue(emp.id, 'comm', e.target.value)}
                        className="w-24 text-xs font-bold text-slate-700 border border-slate-200 px-2 py-1 focus:outline-none focus:border-emerald-500 bg-emerald-50/50" 
                      />
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className="text-sm font-black text-emerald-700">${emp.gross.toLocaleString(undefined, {minimumFractionDigits: 2})}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* CheckHQ Master Provisioning Controls */}
      <div className="bg-amber-50 border border-amber-200 p-4 mb-24 flex justify-between items-center shrink-0">
        <div>
          <h3 className="text-[10px] font-black text-amber-900 uppercase tracking-widest flex items-center gap-2">
            <i className="fa-solid fa-server"></i> CheckHQ Diagnostics & Provisioning
          </h3>
          <p className="text-[9px] font-bold text-amber-700 mt-1 uppercase tracking-widest">
            Execute these commands to mint your test company and push un-vaulted employees to the CheckHQ API.
          </p>
        </div>
        <div className="flex gap-3">
          <button 
            onClick={handleMintCompany}
            disabled={isProvisioning}
            className="bg-white border border-amber-300 hover:bg-amber-100 text-amber-900 text-[9px] font-bold py-2 px-4 transition-colors uppercase tracking-widest disabled:opacity-50"
          >
            Mint Corporate Entity
          </button>
          <button 
            onClick={handleMintEmployees}
            disabled={!hasCompiled || isProvisioning}
            className="bg-amber-600 hover:bg-amber-700 text-white border border-amber-700 text-[9px] font-bold py-2 px-4 transition-colors uppercase tracking-widest disabled:opacity-50"
          >
            Sync Selected Roster
          </button>
        </div>
      </div>

      {/* Footer Fixed Bar */}
      <div className="absolute bottom-0 left-0 right-0 bg-slate-900 border-t border-slate-800 p-4 flex justify-between items-center shadow-[0_-10px_40px_-15px_rgba(0,0,0,0.3)] z-20">
        <div className="flex items-center gap-4">
          <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Total Active Liability:</span>
          <span className="text-2xl font-black text-white">${calculateTotalLiability().toLocaleString(undefined, {minimumFractionDigits: 2})}</span>
        </div>
        <div className="flex items-center gap-3">
          <button 
            disabled={!hasCompiled || calculateTotalLiability() === 0}
            onClick={executeCheckHqPayload}
            className="bg-emerald-600 hover:bg-emerald-500 disabled:bg-slate-700 disabled:text-slate-500 text-white text-[10px] font-bold py-2.5 px-8 uppercase tracking-widest transition-colors shadow-sm"
          >
            Deploy CheckHQ Payroll Run
          </button>
        </div>
      </div>

      {/* CHECKHQ API MODAL */}
      {stagingModal.isOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/80 backdrop-blur-sm animate-in fade-in">
          <div className="bg-white border border-slate-300 shadow-2xl w-full max-w-lg overflow-hidden animate-in zoom-in-95">
            <div className="bg-slate-100 p-4 border-b border-slate-200 flex justify-between items-center">
              <div>
                <h2 className="text-sm font-black text-slate-900 uppercase tracking-tight">Embedded Payroll Staging</h2>
                <p className="text-[8px] font-black text-slate-500 uppercase tracking-widest mt-0.5">Powered by Check API Integration</p>
              </div>
              {stagingModal.step !== 'processing' && (
                <button onClick={() => setStagingModal({ isOpen: false, step: 'idle', message: '' })} className="text-slate-400 hover:text-slate-700">
                  <i className="fa-solid fa-xmark text-lg"></i>
                </button>
              )}
            </div>
            
            <div className="p-12 text-center">
              {stagingModal.step === 'processing' && (
                <div className="flex flex-col items-center animate-in fade-in">
                  <div className="w-12 h-12 border-4 border-slate-200 border-t-emerald-500 rounded-full animate-spin mb-6"></div>
                  <h3 className="text-xs font-black text-slate-800 uppercase tracking-widest">Processing Transaction</h3>
                  <p className="text-[10px] font-bold text-slate-500 mt-2 uppercase tracking-widest">{stagingModal.message}</p>
                </div>
              )}

              {stagingModal.step === 'success' && (
                <div className="flex flex-col items-center animate-in zoom-in slide-in-from-bottom-4 duration-500">
                  <div className="w-16 h-16 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center mb-4 shadow-inner">
                    <i className="fa-solid fa-check text-2xl"></i>
                  </div>
                  <h3 className="text-lg font-black text-slate-900 uppercase tracking-widest">Payload Accepted</h3>
                  <p className="text-[10px] font-bold text-slate-500 mt-2 uppercase tracking-widest leading-relaxed max-w-xs mx-auto">
                    {stagingModal.message}
                  </p>
                  <button 
                    onClick={() => { setStagingModal({ isOpen: false }); setHasCompiled(false); }}
                    className="mt-8 w-full bg-slate-900 hover:bg-slate-800 text-white text-[10px] font-bold py-3 uppercase tracking-widest transition-colors shadow-sm"
                  >
                    Return to Command Center
                  </button>
                </div>
              )}

              {stagingModal.step === 'error' && (
                <div className="flex flex-col items-center animate-in zoom-in slide-in-from-bottom-4 duration-500">
                  <div className="w-16 h-16 bg-red-100 text-red-600 rounded-full flex items-center justify-center mb-4 shadow-inner">
                    <i className="fa-solid fa-triangle-exclamation text-2xl"></i>
                  </div>
                  <h3 className="text-lg font-black text-slate-900 uppercase tracking-widest">API Rejected Payload</h3>
                  <p className="text-[10px] font-bold text-slate-500 mt-2 uppercase tracking-widest leading-relaxed max-w-xs mx-auto text-red-600">
                    {stagingModal.message}
                  </p>
                  <button 
                    onClick={() => setStagingModal({ isOpen: false })}
                    className="mt-8 w-full bg-slate-200 hover:bg-slate-300 text-slate-800 text-[10px] font-bold py-3 uppercase tracking-widest transition-colors shadow-sm"
                  >
                    Dismiss & Review Ledger
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

    </div>
  );
}